import type { ExtendedAssetInfo } from "@/components/playground/hooks/useAssetManager";
import type { ResourceMetadata } from "@/services/api/screener";

export interface PlaygroundAssetsFromMetadata {
  initialAssets: string[];
  initialDisplayNames: Record<string, string>;
  initialAssetMetadata: Record<string, ExtendedAssetInfo>;
}

/**
 * Build embedded Playground props from resource metadata (main resource + benchmark).
 */
export function buildPlaygroundAssetsFromMetadata(
  metadata: ResourceMetadata
): PlaygroundAssetsFromMetadata {
  const initialAssets: string[] = [];
  const initialDisplayNames: Record<string, string> = {};
  const initialAssetMetadata: Record<string, ExtendedAssetInfo> = {};

  const addAsset = (
    resourceId: string,
    displayName: string,
    resourceType: string,
    streamType: string
  ) => {
    initialAssets.push(resourceId);
    initialDisplayNames[resourceId] = displayName;
    initialAssetMetadata[resourceId] = {
      displayName,
      resourceId,
      resourceType,
      streamType: (streamType || "default") as ExtendedAssetInfo["streamType"],
    };
  };

  addAsset(
    metadata.resource_id,
    metadata.resource_display_name,
    metadata.resource_type,
    metadata.stream_type
  );

  if (metadata.benchmark_resource_id) {
    addAsset(
      metadata.benchmark_resource_id,
      metadata.benchmark_display_name ||
        metadata.benchmark_name ||
        metadata.benchmark_resource_id,
      metadata.benchmark_resource_type || "benchmark",
      metadata.benchmark_stream_type || "default"
    );
  }

  return { initialAssets, initialDisplayNames, initialAssetMetadata };
}

/**
 * Format an asset ID to a more readable display name
 * For IDs like MS:F00000N9WE:0P00000UGLE, show a more user-friendly version
 * @param assetId Full asset identifier
 * @returns Formatted display name
 */
export function formatAssetDisplayName(assetId: string): string {
  let displayName = assetId;

  // For IDs with colons (like MS:F00000N9WE:0P00000UGLE)
  if (displayName.includes(":")) {
    const parts = displayName.split(":");

    // If we have multiple parts, use a simplified version
    if (parts.length > 2) {
      // Last part is usually the product ID
      displayName = parts[parts.length - 1];

      // If it's a product ID format and we have enough parts, include the preceding part
      if (parts.length > 3 && displayName.startsWith("0P")) {
        displayName = `${parts[parts.length - 2]}:${displayName}`;
      }
    }
  }

  return displayName;
}

/**
 * Create a consistent mapping of assets to display names
 * @param assets Array of asset IDs
 * @returns Object with displayName mappings (colors now handled by useAssetManager)
 */
export function createAssetMappings(assets: string[]): {
  displayNames: Record<string, string>;
} {
  const displayNames: Record<string, string> = {};

  assets.forEach((asset) => {
    // Create a display name
    displayNames[asset] = formatAssetDisplayName(asset);
  });

  return { displayNames };
}

interface PlaygroundUrlOptions {
  chartSettings?: {
    period?: string;
    startDate?: string;
    endDate?: string;
    frequency?: string;
    displayMode?: "index" | "performance";
  };
}

export type ChatPlaygroundOpenAsset = {
  display_name: string;
  resource_id: string;
  resource_type: string;
  stream_type: string;
};

export type ChatPlaygroundOpenPayload = {
  assets: ChatPlaygroundOpenAsset[];
  period?: string;
  start_date?: string;
  end_date?: string;
  frequency?: string;
  display_mode?: "performance" | "index";
};

export function encodePlaygroundAssetsParam(assets: ChatPlaygroundOpenAsset[]): string {
  return assets
    .map(
      (asset) =>
        `${asset.display_name}:::${asset.resource_id}:::${asset.resource_type}:::${asset.stream_type || "default"}`,
    )
    .join(",");
}

/** Deep link to Playground with the same assets, window, frequency, and performance display as a chat chart embed. */
export function buildPerformancePlaygroundUrl(open: ChatPlaygroundOpenPayload): string {
  if (!open.assets.length) {
    return "/playground";
  }
  const queryParts = [
    "layout=columns",
    `assets=${encodeURIComponent(encodePlaygroundAssetsParam(open.assets))}`,
    `display=${encodeURIComponent(open.display_mode === "index" ? "index" : "performance")}`,
  ];
  if (open.frequency) {
    queryParts.push(`frequency=${encodeURIComponent(open.frequency)}`);
  }
  if (open.period === "custom" && open.start_date && open.end_date) {
    queryParts.push("period=custom");
    queryParts.push(`start_date=${encodeURIComponent(open.start_date)}`);
    queryParts.push(`end_date=${encodeURIComponent(open.end_date)}`);
  } else if (open.period) {
    queryParts.push(`period=${encodeURIComponent(open.period)}`);
  }
  return `/playground?${queryParts.join("&")}`;
}

/**
 * Formats a playground URL with consistent handling of stream types and other parameters
 */
export function formatPlaygroundUrl(
  metadata: ResourceMetadata,
  options?: PlaygroundUrlOptions
): {
  url: string;
  state: { state: { layout: string } };
} {
  // Format the main asset string
  const mainAsset = `${metadata.resource_display_name}:::${metadata.resource_id}:::${metadata.resource_type}:::${metadata.stream_type}`;
  let formattedAssets = mainAsset;

  // Add benchmark if available
  if (metadata.benchmark_resource_id) {
    const benchmarkAsset = `${metadata.benchmark_display_name}:::${metadata.benchmark_resource_id}:::${metadata.benchmark_resource_type}:::${metadata.benchmark_stream_type}`;
    formattedAssets += `,${benchmarkAsset}`;
  }

  const queryParts = [
    "layout=columns",
    `assets=${encodeURIComponent(formattedAssets)}`,
  ];

  if (options?.chartSettings?.period) {
    queryParts.push(
      `period=${encodeURIComponent(options.chartSettings.period)}`
    );
  }

  if (options?.chartSettings?.frequency) {
    queryParts.push(
      `frequency=${encodeURIComponent(options.chartSettings.frequency)}`
    );
  }

  if (options?.chartSettings?.displayMode) {
    queryParts.push(
      `display=${encodeURIComponent(options.chartSettings.displayMode)}`
    );
  }

  if (
    options?.chartSettings?.period === "custom" &&
    options.chartSettings.startDate &&
    options.chartSettings.endDate
  ) {
    queryParts.push(
      `start_date=${encodeURIComponent(options.chartSettings.startDate)}`
    );
    queryParts.push(
      `end_date=${encodeURIComponent(options.chartSettings.endDate)}`
    );
  }

  const playgroundUrl = `/playground?${queryParts.join("&")}`;

  return {
    url: playgroundUrl,
    state: { state: { layout: "columns" } },
  };
}
